/**
 * TaskLeaderboardChip — compact rolling-7d task throughput pill mounted
 * at the top of every Pulse profile. Three numbers, no clutter:
 *   • done this week (emerald)
 *   • overdue right now (rose if >0)
 *   • avg TAT in human-friendly units (sky)
 *
 * The same data point is what supervisors use to spot stragglers; tap on
 * the strip to scroll the "Tasks I oversee" / "Tasks assigned to me"
 * cards into view (anchor-link UX, zero client routing).
 */
import React, { useEffect, useState } from 'react';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Activity, CheckCircle2, AlertTriangle, Timer } from 'lucide-react';

const fmtTat = (m) => {
  if (m === null || m === undefined) return '—';
  if (m < 60) return `${m}m`;
  const h = m / 60;
  if (h < 24) return `${Math.round(h * 10) / 10}h`;
  return `${Math.round((h / 24) * 10) / 10}d`;
};

export default function TaskLeaderboardChip({ email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    const t = localStorage.getItem('session_token');
    fetch(`${API}/pollen/pulse/task-leaderboard?email=${encodeURIComponent(email)}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [email]);

  if (loading || !data) return null;
  const overdueTint = (data.overdue_now || 0) > 0
    ? 'bg-rose-50 text-rose-800 border-rose-200'
    : 'bg-stone-50 text-stone-600 border-stone-200';

  return (
    <Card data-testid="task-leaderboard-chip">
      <CardContent className="p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">
          <Activity className="w-3.5 h-3.5 text-amber-600" />
          This week
        </div>
        <a
          href="#tasks-assigned-card"
          className="inline-flex items-center gap-1 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-900 px-2 py-1 text-xs font-medium hover:bg-emerald-100"
          data-testid="leaderboard-done"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span className="tabular-nums">{data.done_7d || 0}</span>
          <span className="text-[10px] text-emerald-700">done</span>
        </a>
        <a
          href="#tasks-assigned-card"
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium ${overdueTint}`}
          data-testid="leaderboard-overdue"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="tabular-nums">{data.overdue_now || 0}</span>
          <span className="text-[10px] opacity-80">overdue</span>
        </a>
        <span
          className="inline-flex items-center gap-1 rounded-md border bg-sky-50 border-sky-200 text-sky-900 px-2 py-1 text-xs font-medium"
          title="Average time-to-close for tasks finished this week"
          data-testid="leaderboard-tat"
        >
          <Timer className="w-3.5 h-3.5" />
          <span className="tabular-nums">{fmtTat(data.avg_tat_minutes_7d)}</span>
          <span className="text-[10px] text-sky-700">avg TAT</span>
        </span>
      </CardContent>
    </Card>
  );
}
